import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Menu, X, Truck, Sun, Moon, User, LogOut, UserCog, ShoppingBag, Tag, Sparkles, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { coupons as couponsApi, type CouponAPI } from '../api/client';
import { ZeroSmokeLogo } from './Logo';
import { brands } from '@shared/brands';
import AnimatedBadge from './AnimatedBadge';
import { getBadgeForOrders } from '../lib/badges';

interface BannerSlide {
  id: string;
  icon: 'truck' | 'tag' | 'sparkle';
  text: string;
  highlight?: string;
}

function couponToSlide(c: CouponAPI): BannerSlide {
  const discount = c.discount_type === 'percentage'
    ? `${c.discount_value}% OFF`
    : `\u20b9${c.discount_value.toLocaleString('en-IN')} OFF`;
  const min = c.min_order_value
    ? ` on orders above \u20b9${c.min_order_value.toLocaleString('en-IN')}`
    : '';
  return {
    id: c.id,
    icon: 'tag',
    text: `Use code`,
    highlight: `${c.code} \u2014 ${discount}${min}`,
  };
}

export default function Navbar() {
  const { totalItems } = useCart();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navBadge = user ? getBadgeForOrders(user.order_count) : null;
  const initials = user
    ? (`${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() || user.email[0]?.toUpperCase() || 'U')
    : 'U';
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [bannerSlides, setBannerSlides] = useState<BannerSlide[]>([
    { id: 'shipping', icon: 'truck', text: 'Free shipping across Country on orders of \u20b95,000 or more' },
  ]);
  const location = useLocation();

  useEffect(() => {
    couponsApi.active().then((list) => {
      const base: BannerSlide = { id: 'shipping', icon: 'truck', text: 'Free shipping across Country on orders of \u20b95,000 or more' };
      setBannerSlides([base, ...list.map(couponToSlide)]);
    }).catch(() => {});
  }, []);

  const slideCount = bannerSlides.length;

  const nextSlide = useCallback(() => {
    setBannerIdx((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  // Auto-rotate when there are 2+ slides
  useEffect(() => {
    if (slideCount <= 1) return;
    const timer = setInterval(nextSlide, 3500);
    return () => clearInterval(timer);
  }, [slideCount, nextSlide]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenus = () => {
    setMobileOpen(false);
  };

  const handleNavClick = () => {
    closeMenus();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Store is rendered separately as a dropdown so the brand list can hang
  // off it; the others are plain links.
  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/rewards', label: 'Rewards' },
    { to: '/wholesale', label: 'Wholesale' },
    { to: '/blog', label: 'Blog' },
    { to: '/faq', label: 'FAQ' },
    { to: '/about', label: 'About' },
  ];

  const isStoreActive =
    location.pathname === '/store' ||
    location.pathname.startsWith('/brand/');

  const [mobileStoreOpen, setMobileStoreOpen] = useState(false);

  const slide = bannerSlides[bannerIdx] ?? bannerSlides[0];
  const SlideIcon = slide?.icon === 'tag' ? Tag : slide?.icon === 'sparkle' ? Sparkles : Truck;

  return (
    <>
      {/* Top banner carousel */}
      <div
        className="relative bg-emerald-600 text-white overflow-hidden cursor-pointer select-none py-2"
        onClick={slideCount > 1 ? nextSlide : undefined}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.id}
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -16, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="flex items-center justify-center gap-1.5 px-3 sm:px-4 text-xs sm:text-sm font-medium text-center flex-wrap"
          >
            <SlideIcon size={14} className="flex-shrink-0" />
            <span>{slide.text}</span>
            {slide.highlight && (
              <span className="font-extrabold tracking-wide bg-white/15 px-2 py-0.5 rounded text-xs sm:text-sm">
                {slide.highlight}
              </span>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Progress bar for multi-slide */}
        {slideCount > 1 && (
          <motion.div
            key={`progress-${bannerIdx}`}
            className="absolute bottom-0 left-0 h-0.5 bg-white/40"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 3.5, ease: 'linear' }}
          />
        )}
      </div>

      <motion.header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? `glass border-b shadow-sm ${isDark ? 'border-gray-800' : 'border-gray-100'}`
            : isDark ? 'bg-gray-950' : 'bg-white'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" onClick={closeMenus} className="flex-shrink-0 flex items-center gap-2">
              <ZeroSmokeLogo size={34} />
              <span className="text-xl font-extrabold tracking-tight"><span className="text-emerald-500">Zero</span><span className={isDark ? 'text-white' : 'text-gray-900'}>Smoke</span></span>
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-8">
              {/* Home */}
              <Link
                to="/"
                onClick={handleNavClick}
                className={`text-sm font-medium transition-colors hover:text-emerald-500 ${
                  location.pathname === '/'
                    ? 'text-emerald-500'
                    : isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Home
              </Link>

              {/* Store dropdown */}
              <div className="relative group">
                <Link
                  to="/store"
                  onClick={handleNavClick}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-emerald-500 ${
                    isStoreActive
                      ? 'text-emerald-500'
                      : isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Store
                  <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                </Link>
                <div
                  className={`absolute left-1/2 -translate-x-1/2 top-full mt-1 w-56 rounded-xl border shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 ${
                    isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'
                  }`}
                >
                  <Link
                    to="/store"
                    onClick={handleNavClick}
                    className={`block px-4 py-2.5 text-sm font-semibold border-b transition-colors ${
                      isDark
                        ? 'text-white border-gray-800 hover:bg-gray-800 hover:text-emerald-400'
                        : 'text-gray-900 border-gray-100 hover:bg-gray-50 hover:text-emerald-600'
                    }`}
                  >
                    All Products
                  </Link>
                  {brands.map((b) => (
                    <Link
                      key={b.slug}
                      to={`/brand/${b.slug}`}
                      onClick={handleNavClick}
                      className={`block px-4 py-2 text-sm transition-colors ${
                        isDark
                          ? 'text-gray-300 hover:bg-gray-800 hover:text-emerald-400'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-emerald-600'
                      }`}
                    >
                      {b.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Remaining nav links (Blog, FAQ, About) */}
              {navLinks.slice(1).map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleNavClick}
                  className={`text-sm font-medium transition-colors hover:text-emerald-500 ${
                    location.pathname === link.to
                      ? 'text-emerald-500'
                      : isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                aria-label="Toggle theme"
              >
                <motion.div
                  key={isDark ? 'moon' : 'sun'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isDark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-gray-700" />}
                </motion.div>
              </button>

              {/* User / Login */}
              {user ? (
                <div className="relative group">
                  <button
                    className={`flex items-center gap-2 rounded-full transition-shadow sm:pl-3 sm:pr-2 sm:py-1 sm:border ${isDark ? 'sm:border-gray-700 sm:bg-gradient-to-r sm:from-emerald-500/25 sm:via-gray-900 sm:to-amber-500/25 sm:hover:shadow-lg sm:hover:shadow-black/30' : 'sm:border-gray-200 sm:bg-gradient-to-r sm:from-emerald-100 sm:via-white sm:to-amber-100 sm:hover:shadow-md'}`}
                    aria-label="Account"
                  >
                    {/* Coin balance text — hidden on phones to keep the pill compact (badge-only). */}
                    <div className="hidden sm:flex flex-col items-start justify-center leading-tight">
                      <span className={`text-[10px] font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>ZS Coins</span>
                      <span className="text-sm font-bold text-amber-500">{(user.coin_balance ?? 0).toLocaleString('en-IN')}</span>
                    </div>
                    {navBadge ? (
                      <AnimatedBadge src={navBadge.src} glow={navBadge.glow} size={32} variant="subtle" alt={`${navBadge.name} badge`} />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold select-none bg-gradient-to-br from-emerald-400 to-teal-600 shadow-sm">
                        {initials}
                      </div>
                    )}
                    <ChevronDown size={16} className={`hidden sm:block transition-transform group-hover:rotate-180 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  </button>
                  <div className={`absolute right-0 mt-1 w-44 rounded-xl border shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                    <div className={`px-4 py-3 border-b text-sm ${isDark ? 'border-gray-800 text-gray-300' : 'border-gray-100 text-gray-700'}`}>
                      {user.first_name || user.email}
                    </div>
                    <Link
                      to="/profile"
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-emerald-400 hover:bg-gray-800' : 'text-gray-600 hover:text-emerald-600 hover:bg-gray-50'}`}
                    >
                      <UserCog size={14} /> My Profile
                    </Link>
                    <Link
                      to="/orders"
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-emerald-400 hover:bg-gray-800' : 'text-gray-600 hover:text-emerald-600 hover:bg-gray-50'}`}
                    >
                      <ShoppingBag size={14} /> My Orders
                    </Link>
                    <button
                      onClick={logout}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-red-400 hover:bg-gray-800' : 'text-gray-600 hover:text-red-600 hover:bg-gray-50'}`}
                    >
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <Link
                  to="/login"
                  className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                  aria-label="Login"
                >
                  <User size={20} className={isDark ? 'text-gray-300' : 'text-gray-700'} />
                </Link>
              )}

              <Link
                to="/cart"
                className={`relative p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                aria-label="Cart"
              >
                <ShoppingCart size={20} className={isDark ? 'text-gray-300' : 'text-gray-700'} />
                {totalItems > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-emerald-600 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    {totalItems}
                  </motion.span>
                )}
              </Link>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`md:hidden p-2 rounded-full transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                aria-label="Menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>


        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className={`md:hidden overflow-hidden border-t ${isDark ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-100'}`}
            >
              <div className="px-4 py-4 space-y-3">
                {/* Home */}
                <Link
                  to="/"
                  onClick={handleNavClick}
                  className={`block py-2 text-base font-medium transition-colors ${
                    location.pathname === '/'
                      ? 'text-emerald-500'
                      : isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  Home
                </Link>

                {/* Store with expandable brand list */}
                <div>
                  <button
                    onClick={() => setMobileStoreOpen((o) => !o)}
                    className={`w-full flex items-center justify-between py-2 text-base font-medium transition-colors ${
                      isStoreActive
                        ? 'text-emerald-500'
                        : isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                    aria-expanded={mobileStoreOpen}
                  >
                    <span>Store</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${mobileStoreOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {mobileStoreOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-3 mt-1 space-y-1 border-l border-emerald-500/30">
                          <Link
                            to="/store"
                            onClick={handleNavClick}
                            className={`block py-2 text-sm font-semibold transition-colors ${
                              location.pathname === '/store'
                                ? 'text-emerald-500'
                                : isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}
                          >
                            All Products
                          </Link>
                          {brands.map((b) => (
                            <Link
                              key={b.slug}
                              to={`/brand/${b.slug}`}
                              onClick={handleNavClick}
                              className={`block py-2 text-sm transition-colors ${
                                location.pathname === `/brand/${b.slug}`
                                  ? 'text-emerald-500'
                                  : isDark ? 'text-gray-400' : 'text-gray-600'
                              }`}
                            >
                              {b.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Remaining nav links */}
                {navLinks.slice(1).map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={handleNavClick}
                    className={`block py-2 text-base font-medium transition-colors ${
                      location.pathname === link.to
                        ? 'text-emerald-500'
                        : isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                {/* Mobile login / account */}
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={closeMenus}
                      className={`block py-2 text-base font-medium transition-colors ${
                        location.pathname === '/profile'
                          ? 'text-emerald-500'
                          : isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      My Profile
                    </Link>
                    <Link
                      to="/orders"
                      onClick={closeMenus}
                      className={`block py-2 text-base font-medium transition-colors ${
                        location.pathname === '/orders'
                          ? 'text-emerald-500'
                          : isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      My Orders
                    </Link>
                    <button
                      onClick={() => { logout(); closeMenus(); }}
                      className={`block w-full text-left py-2 text-base font-medium transition-colors ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={closeMenus}
                    className={`block py-2 text-base font-medium text-emerald-500`}
                  >
                    Login / Register
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
}
